import { Node, Edge } from 'reactflow';
import { NodeGroup } from '../types/groupTypes';
import { findConnectedComponents } from './graphAlgorithms';
import { getNextColor } from './groupColors';

const GROUP_PADDING = 25;

function getNextGroupId(existingGroups: NodeGroup[]): string {
  const usedIds = existingGroups
    .map(g => parseInt(g.id, 10))
    .filter(n => !isNaN(n));
  const maxId = usedIds.length > 0 ? Math.max(...usedIds) : 0;
  return String(maxId + 1);
}

function calculateBounds(nodeIds: string[], allNodes: Node[]) {
  const groupNodes = allNodes.filter(node => nodeIds.includes(node.id));

  if (groupNodes.length === 0) {
    return { position: { x: 0, y: 0 }, dimensions: { width: 300, height: 150 } };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  groupNodes.forEach(node => {
    const nodeWidth = node.width || 250;
    const nodeHeight = node.height || 120;
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + nodeWidth);
    maxY = Math.max(maxY, node.position.y + nodeHeight);
  });

  return {
    position: { x: minX - GROUP_PADDING, y: minY - GROUP_PADDING },
    dimensions: { width: maxX - minX + GROUP_PADDING * 2, height: maxY - minY + GROUP_PADDING * 2 },
  };
}

/**
 * Reconcilia grupos con las conexiones (edges) existentes.
 * Fusiona grupos cuyos nodos están conectados en un solo grupo.
 * Crea nuevos grupos para nodos sin grupo.
 */
export function reconcileGroupsWithEdges(
  loadedGroups: NodeGroup[],
  allNodes: Node[],
  allEdges: Edge[]
): NodeGroup[] {
  const allNodeIds = allNodes.map(n => n.id);

  // Encontrar componentes conectados entre TODOS los nodos
  const components = findConnectedComponents(allNodeIds, allEdges);

  const reconciledGroups: NodeGroup[] = [];

  for (const component of components) {
    // Encontrar todos los grupos que contienen nodos de este componente
    const relevantGroups = loadedGroups.filter(g =>
      g.nodeIds.some(nid => component.includes(nid))
    );

    const bounds = calculateBounds(component, allNodes);

    if (relevantGroups.length === 0) {
      // Nodos sin grupo - crear uno nuevo
      reconciledGroups.push({
        id: getNextGroupId([...loadedGroups, ...reconciledGroups]),
        number: reconciledGroups.length + 1,
        nodeIds: component,
        color: getNextColor(reconciledGroups.length),
        position: bounds.position,
        dimensions: bounds.dimensions,
        createdAt: Date.now(),
      });
    } else if (relevantGroups.length === 1) {
      // Un solo grupo - actualizar nodeIds al componente completo
      const group = relevantGroups[0];
      reconciledGroups.push({
        ...group,
        nodeIds: component,
        position: bounds.position,
        dimensions: bounds.dimensions,
      });
    } else {
      // Múltiples grupos necesitan fusionarse - mantener el más antiguo
      const sorted = [...relevantGroups].sort((a, b) => a.createdAt - b.createdAt);
      const keepGroup = sorted[0];

      // Tomar instruction/leadStatus/productKeys del primer grupo que los tenga
      const instruction = sorted.find(g => g.instruction)?.instruction;
      const leadStatus = sorted.find(g => g.leadStatus)?.leadStatus;
      const productKeys = sorted.find(g => g.productKeys?.length)?.productKeys;

      reconciledGroups.push({
        ...keepGroup,
        nodeIds: component,
        instruction: instruction || keepGroup.instruction,
        leadStatus: leadStatus || keepGroup.leadStatus,
        productKeys: productKeys || keepGroup.productKeys,
        position: bounds.position,
        dimensions: bounds.dimensions,
      });
    }
  }

  // Renumerar (solo number, no id)
  const sorted = [...reconciledGroups].sort((a, b) => a.createdAt - b.createdAt);
  return sorted.map((group, index) => ({
    ...group,
    number: index + 1,
  }));
}
